import { DocumentTypes } from "../../../common/enums/discipline.enum";

export const documentTypeShortCode: Record<number, string> = {
  [DocumentTypes.EPOC]: "EPOC",
  [DocumentTypes.EPOCST]: "EPOC-ST",
  [DocumentTypes.ProgressReport]: "PR",
  [DocumentTypes.ProgressReportST]: "PR-ST",
  [DocumentTypes.DCProgressReport]: "DC",
  [DocumentTypes.DCProgressReportST]: "DC-ST",
  [DocumentTypes.RecertificationAndUpdatedPlanOfCare]: "RC",
  [DocumentTypes.RecertificationAndUpdatedPlanOfCareST]: "RC-ST",
  [DocumentTypes.ReEvaluationAndUpdatedPlanOfCare]: "Re-Eval",
  [DocumentTypes.SupplementalEvaluationAndPlanOfCare]: "Supp Eval",
  [DocumentTypes.AddendumNote]: "Addendum",
};

export const interruptedStayMergeConstants = {
  interruptOrMergeButtonText: "Interrupt or Merge Admission",
  sectionHeader: "Interrupted/Merged Admissions",
  faLineLabel: "Interrupted/Merged Stay",
  revertLinkText: "Revert",
  overrideIndicatorLabel: "Override",
  systemUserName: "System",
  mergeButtonText: "Merge",
  mergeButtonTooltip:
    "Interrupting/Merging an admission will merge this admission with the prior admission date.",
  markAsInterruptedStayCheckboxLabel: "Mark as Interrupted Stay",
  maxInterruptedStayLoaDays: 3,
  dischargeReportAlreadyCompleteHint: "DC is Completed",
  unableToLoadMergeDetails: "Unable to load merge details for this facility admission.",
  unableToMergeAdmission: "Unable to merge this facility admission.",
  mergeFlowNotStarted: "Merge flow was not started.",
  unableToCheckRevertEligibility:
    "Unable to check revert eligibility for this facility admission.",
  unableToRevertAdmission: "Unable to revert this facility admission.",
};

export const mergeAdmissionModalConfig = (
  newAdmissionDate: string,
  previousAdmissionDate: string
) => ({
  title: "Interrupted Stay/Merge Admission",
  message: `You are choosing to merge this admission on ${newAdmissionDate} with the previous admission date on ${previousAdmissionDate}. This will result in only one facility admission with the admission date of ${previousAdmissionDate}`,
  buttonText: "Merge",
  cancelText: "Cancel",
  leftAlign: true,
});

export const revertMergeAdmissionModalConfig = (
  priorFromDate: string,
  priorThroughDate: string,
  currentFromDate: string
) => ({
  title: "Revert Interrupted Stay/Merged Admission",
  message: `You are choosing to revert this interrupted/merged stay. This will split the admission into two separate admissions with the dates of ${priorFromDate} - ${priorThroughDate} and ${currentFromDate} - Ongoing`,
  buttonText: "Revert",
  cancelText: "Cancel",
  leftAlign: true,
});

export const overrideInterruptedStayModalConfig = {
  title: "Override Interrupted Stay?",
  message:
    "The duration of the leave of absence is greater than 3 days which is beyond the federal regulation for the PDPM Interrupted Stay Policy.<br><br>Are you sure you wish to override?<br><br>If this patient's payer did not follow the interrupted stay policy, and you wish to continue with the merging of these admissions despite the admission dates being greater than 3 days apart, choose Override and Apply.",
  buttonText: "Override and Apply",
  cancelText: "Cancel",
  leftAlign: true,
  isError: true,
};

export const interruptedStayMergeReasonMessages = {
  subsequentAdmissionExists: (currentAdmissionDate: string) =>
    `Since these admissions were merged, there has been a subsequent admission on ${currentAdmissionDate}.`,
};

const buildReasonListMessage = (intro: string, reasons: string[]) =>
  `${intro}<ul style="text-align: left; margin: 12px 0 12px 24px; list-style: disc;">${reasons
    .map((reason) => `<li>${reason}</li>`)
    .join("")}</ul>`;

const buildGroupedReasonSection = (heading: string, lines: string[]) =>
  lines.length
    ? `<div style="margin-bottom: 12px;">${heading}<ul style="text-align: left; margin: 4px 0 4px 24px; list-style: disc;">${lines
        .map((line) => `<li>${line}</li>`)
        .join("")}</ul></div>`
    : "";

const buildGroupedDocumentSection = (
  heading: string,
  groups: { header: string; lines: string[] }[]
) =>
  groups.length
    ? `<div style="margin-bottom: 12px;">${heading}${groups
        .map(
          (group) =>
            `<div style="margin: 8px 0 4px 12px;"><b>${group.header}</b><ul style="text-align: left; margin: 4px 0 4px 24px; list-style: disc;">${group.lines
              .map((line) => `<li>${line}</li>`)
              .join("")}</ul></div>`
        )
        .join("")}</div>`
    : "";

export const mergeAdmissionBlockedModalConfig = (reason: string) => ({
  title: "Unable to Merge Admission",
  message: reason,
  buttonText: "Continue",
  hideCancelButton: true,
  isError: true,
  leftAlign: true,
});

export const mergeAdmissionSuccessModalConfig = () => ({
  title: "Admission Merged",
  message: "The facility admissions have been merged successfully.",
  buttonText: "Continue",
  hideCancelButton: true,
  leftAlign: true,
});

export const errorRevertingMergedAdmissionModalConfig = (reasons: string[]) => ({
  title: "Error Reverting Interrupted Stay/Merged Admission",
  message: buildReasonListMessage(
    "Unable to revert facility admission merge due to the following reasons:",
    reasons
  ),
  buttonText: "Continue",
  hideCancelButton: true,
  isError: true,
  leftAlign: true,
});

export const revertValidationBlockedModalConfig = (
  fromDate: string,
  billingLines: string[],
  documentGroups: { header: string; lines: string[] }[],
  episodeLines: string[]
) => ({
  title: "Error Reverting Interrupted Stay/Merged Admission",
  message:
    buildGroupedReasonSection(`Therapy Billings performed after ${fromDate}:`, billingLines) +
    buildGroupedDocumentSection(
      `The following document(s) reached Complete (or higher) status on or after ${fromDate}. Change these back to Initiated/Draft status or delete them before reverting.`,
      documentGroups
    ) +
    buildGroupedReasonSection(
      `The Plan of Care start date (SOC) on the following therapy episode(s) was changed after Merge. Update these episode dates first.`,
      episodeLines
    ),
  buttonText: "Continue",
  hideCancelButton: true,
  isError: true,
  leftAlign: true,
});

export const openPreviousEpisodeModalConfig = {
  title: "Open Previous Episode?",
  message:
    "The disciplines below already have a closed episode and discharge report on the previous admission. Select any you'd like to reopen and continue the previous Plan of Care for - anything left unchecked will remain closed.",
  buttonText: "Continue",
  cancelText: "Cancel",
  leftAlign: true,
};

export const dischargeNoteCompleteMergeNoticeModalConfig = (disciplineNames: string[]) => ({
  title: "Discharge Note Complete",
  message: `The current discharge note is already complete for ${disciplineNames.join(
    ", "
  )} and cannot be automatically removed. To continue with the previous plan of care, you must manage the discharge document.`,
  buttonText: "Continue",
  hideCancelButton: true,
  leftAlign: true,
});

export const revertAdmissionSuccessModalConfig = () => ({
  title: "Admission Reverted",
  message: "The facility admission merge has been reverted successfully.",
  buttonText: "Continue",
  hideCancelButton: true,
  leftAlign: true,
});
